import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Cookies from 'js-cookie';
import api from '../utils/api';
import { apiErrorMessage } from '../utils/apiErrors';
import { useCognitiveFlows } from '../context/CognitiveFlowsContext';
import { Button, ConfirmDialog } from '../components/shared/Modal';
import { EditIconButton, DeleteIconButton } from '../components/shared/IconActionButton';
import { ts, PageHeader, Monogram, RolePill, SelfTag, AccessChips, EmptyState, SkeletonRows, PlusIcon, hoverRow } from '../components/team/teamUi';
import Pagination from '../components/shared/Pagination';
import WorkspaceFormModal from '../components/team/WorkspaceFormModal';
import MemberAccessModal from '../components/team/MemberAccessModal';
import { defaultWorkspaceLabel } from '../utils/defaultWorkspace';
import TransferOwnershipDialog from '../components/team/TransferOwnershipDialog';
import DeleteWorkspaceDialog from '../components/team/DeleteWorkspaceDialog';

// Settings → Workspace / Teams. One page, two views: `workspace` lists the
// account's workspaces and their default ad accounts, `teams` lists members,
// their role and access, and any pending invites. Both are a page header with a
// primary action plus one full-width table; every create, edit and destructive
// action happens in a dialog (see components/shared/Modal).
//
// Who sees what is decided by the server and arrives on the /team payload
// (`canManageAccount`, `canTransferOwnership`), never inferred from a cookie:
//  - owner / admin  full page.
//  - everyone else  the Teams roster, read-only. Members can see who is on
//    their account; they just can't change anything.
//
// Roles are changed in the member dialog, not from row icons: promoting someone
// is a decision about that person, and it belongs next to everything else about
// them. Ownership is the exception — it is a transfer, with its own dialog.

const normalizeEmail = (value) => String(value || '').trim().toLowerCase();

const formatDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

export default function CognitiveFlowsTeamPage({ view = 'workspace' }) {
    const { ownerResolved, isOwner, accountName, revalidateStore } = useCognitiveFlows();
    const [team, setTeam] = useState(null); // { workspaces, members, invites }
    const [adAccounts, setAdAccounts] = useState({ google: { connected: false, accounts: [] }, facebook: { connected: false, accounts: [] } });
    const [wsModal, setWsModal] = useState(null); // { mode: 'create' } | { mode: 'edit', workspace }
    const [memberModal, setMemberModal] = useState(null); // { mode: 'invite' } | { mode: 'edit', member }
    const [transfer, setTransfer] = useState(null); // { email, mode: 'transfer' | 'claim' }
    const [confirm, setConfirm] = useState(null); // { kind, … } — see runConfirm
    const [busy, setBusy] = useState(false); // only one dialog is open at a time

    // The Workspaces table is paged server-side; `team.workspaces` stays the FULL
    // list because the Teams view resolves grant chips against it and the member
    // dialog has to offer every workspace, not just the page on screen.
    const [wsQuery, setWsQuery] = useState('');   // what's typed
    const [wsSearch, setWsSearch] = useState(''); // debounced, sent to the API
    const [wsPageNum, setWsPageNum] = useState(1);
    const [wsLimit, setWsLimit] = useState(20);
    const [wsPage, setWsPage] = useState(null);   // { rows, total, page, limit, pageCount }
    const [wsLoading, setWsLoading] = useState(false);

    const loadTeam = useCallback(async () => {
        try {
            const { data } = await api.get('/team');
            setTeam(data);
        } catch (err) {
            if (err?.response?.status !== 403) toast.error(apiErrorMessage(err, 'Failed to load team'));
            setTeam({ workspaces: [], members: [], invites: [], forbidden: true });
        }
    }, []);

    const loadWorkspaces = useCallback(async () => {
        setWsLoading(true);
        try {
            const { data } = await api.get('/workspaces', {
                params: { page: wsPageNum, limit: wsLimit, search: wsSearch },
            });
            const rows = data.workspaces || [];
            setWsPage({
                rows,
                total: data.total ?? rows.length,
                page: data.page ?? wsPageNum,
                limit: data.limit ?? wsLimit,
                pageCount: data.pageCount ?? 1,
            });
            // The server clamps an out-of-range page (deleting the last row of the
            // final page, or a search that narrows past it) — follow it, or the
            // pager and the rows would disagree until the next manual click.
            if (data.page && data.page !== wsPageNum) setWsPageNum(data.page);
        } catch (err) {
            if (err?.response?.status !== 403) toast.error(apiErrorMessage(err, 'Failed to load workspaces'));
            setWsPage({ rows: [], total: 0, page: 1, limit: wsLimit, pageCount: 0 });
        } finally {
            setWsLoading(false);
        }
    }, [wsPageNum, wsLimit, wsSearch]);

    const selfEmail = normalizeEmail(Cookies.get('user_email'));
    // Not a denial — a plain member gets the roster, read-only. The cookie seed
    // recognises both role markers, so a seeded "member" is already definitive
    // and the page commits on the first paint. `resolving` is the case the seed
    // CAN'T answer (Shopify OAuth sessions carry no permissions cookie): commit
    // to neither view until /meta lands, or an owner gets flashed the notice.
    const readOnly = ownerResolved && !isOwner;
    const resolving = !ownerResolved;

    // The roster loads for everyone; the manager-only data does not.
    useEffect(() => { loadTeam(); }, [loadTeam]);
    useEffect(() => { if (!readOnly) loadWorkspaces(); }, [readOnly, loadWorkspaces]);

    // Debounced so typing doesn't fire a request per keystroke. A new term always
    // restarts at page 1 — otherwise a narrowing search lands on an empty page 4.
    useEffect(() => {
        const t = setTimeout(() => { setWsSearch(wsQuery.trim()); setWsPageNum(1); }, 300);
        return () => clearTimeout(t);
    }, [wsQuery]);

    // Connected ads accounts (shop-level) power the per-workspace default pickers.
    // `all=1` because this is the form that CHOOSES the workspace's account — it
    // must see the shop's whole inventory, not the current workspace's lock.
    useEffect(() => {
        if (readOnly) return;
        api.get('/connected-accounts', { params: { all: 1 } })
            .then(({ data }) => setAdAccounts({
                google: data?.google || { connected: false, accounts: [] },
                facebook: data?.facebook || { connected: false, accounts: [] },
            }))
            .catch(() => {});
    }, [readOnly]);

    // Workspaces are a manager surface; a plain member opening that tab still
    // gets the notice. The Teams tab below shows them the roster instead.
    if (readOnly && view !== 'teams') {
        return (
            <div style={ts.page}>
                <div style={ts.surface}>
                    <p style={ts.notice}>Only an account owner or admin can manage workspaces.</p>
                </div>
            </div>
        );
    }

    if (resolving) {
        return (
            <div style={ts.page}>
                <div style={ts.surface}>
                    <p style={ts.notice}>Loading…</p>
                </div>
            </div>
        );
    }

    const workspaces = team?.workspaces || [];
    // The default workspace's row (id 'account') — a separate field, never
    // inside workspaces[], whose ids are real ObjectIds everywhere else.
    const accountWs = team?.accountWorkspace || null;
    const members = team?.members || [];
    const invites = team?.invites || [];
    const loading = team === null;
    // Straight from the account's `owner:<shop>` markers. An account should have
    // exactly one; zero and "more than one" are both legacy states the banners
    // below explain and offer to fix.
    const ownerCount = team?.ownerCount ?? 0;
    // The server's verdict on whether this caller may act at all — owners and
    // admins yes, everyone else read-only. The server gates every mutation too;
    // this is the UI half.
    const canManageAccount = team?.canManageAccount !== false;
    // Handing the account over is the owner's alone (platform staff included) —
    // except on an ownerless account, where any manager may name the first one.
    // Otherwise a customer whose account predates this model would have to ask us
    // to unlock their own billing.
    const canAssignOwner = team?.canTransferOwnership === true || (ownerCount === 0 && canManageAccount);

    const workspaceName = (id) => workspaces.find((w) => w.id === id)?.name || id;
    const googleAccounts = adAccounts.google.accounts || [];
    const metaAccounts = adAccounts.facebook.accounts || [];
    const hasAdsAccounts = googleAccounts.length > 0 || metaAccounts.length > 0;

    const googleLabel = (id) => (id ? (googleAccounts.find((a) => a.id === id)?.label || id) : null);
    const metaLabel = (id) => {
        if (!id) return null;
        const a = metaAccounts.find((x) => x.id === id);
        if (!a) return id;
        return a.name ? `${a.name} (${a.id})` : a.id;
    };
    const pixelLabel = (w) => {
        if (!w.defaultMetaPixel) return null;
        const p = metaAccounts.find((a) => a.id === w.defaultMetaAdsAccount)?.pixels?.find((x) => x.id === w.defaultMetaPixel);
        return p?.name || w.defaultMetaPixel;
    };
    // The default workspace counts its accountLevel grants; a real workspace
    // counts its id grants. Owners and admins still count everywhere — the
    // server expands their rows to accountLevel + every workspace id.
    const peopleCount = (id) => members.filter((m) => (
        id === 'account' ? m.accountLevel : (m.workspaceIds || []).includes(id)
    )).length;

    // —— Mutations (endpoints, payloads and toasts unchanged) ——

    // `applyToExisting` is set by the confirmation step below, never on the first
    // attempt: changing a workspace's ad account retargets every agent and data
    // source in it, so the server answers 409 with the affected counts and we come
    // back only once the user has seen the number.
    const saveWorkspace = async (form, applyToExisting = false) => {
        const { name, website, google, meta, pixel, primaryMetrics } = form;
        const editing = wsModal?.mode === 'edit';
        const editingDefault = editing && wsModal?.workspace?.isDefault === true;
        const noun = editingDefault ? 'Default workspace' : 'Workspace';
        setBusy(true);
        const tid = toast.loading(editing ? `Saving ${editingDefault ? 'default workspace' : 'workspace'}…` : 'Creating workspace…');
        const payload = {
            name,
            websiteUrl: website || null,
            defaultGoogleAdsAccount: google || null,
            defaultMetaAdsAccount: meta || null,
            defaultMetaPixel: pixel || null,
            primaryMetrics: primaryMetrics || [],
            ...(applyToExisting ? { applyToExisting: true } : {}),
        };
        try {
            if (editing) {
                // The default workspace PATCHes /workspaces/account — same
                // payload, same confirm loop; the server ignores its name.
                const { data } = await api.patch(`/workspaces/${wsModal.workspace.id}`, payload);
                const n = (data?.updated?.workflows || 0) + (data?.updated?.dataSources || 0);
                toast.success(n ? `${noun} updated — ${n} item${n === 1 ? '' : 's'} moved to the new account.` : `${noun} updated`, { id: tid });
            } else {
                const { data } = await api.post('/workspaces', payload);
                toast.success(`Workspace "${data.workspace?.name}" created — it's now in your workspace dropdown.`, { id: tid });
            }
            setWsModal(null);
            revalidateStore();
            loadTeam();
            loadWorkspaces();
        } catch (err) {
            if (err?.response?.status === 409 && err.response.data?.code === 'WORKSPACE_ADS_ACCOUNT_IN_USE') {
                toast.dismiss(tid);
                setConfirm({
                    kind: 'applyWorkspaceAccounts',
                    counts: err.response.data.counts || { workflows: 0, dataSources: 0 },
                    // The WHOLE form, not a hand-listed subset: runConfirm re-calls
                    // saveWorkspace with it, and a subset silently re-posts
                    // primaryMetrics: [] over the metrics just picked.
                    form,
                    failMessage: 'Failed to update workspace',
                });
                return;
            }
            toast.error(apiErrorMessage(err, (editing ? 'Failed to update workspace' : 'Failed to create workspace')), { id: tid });
        } finally {
            setBusy(false);
        }
    };

    // Creating a workspace with ad accounts pinned wires them into the starter
    // agents and default data sources the moment the workspace exists — pause
    // for a confirmation before that happens. Setting a default that was empty
    // on an EXISTING workspace deserves the same pause: the choice is set-once
    // (server: WORKSPACE_ADS_IMMUTABLE), so this confirm is the last exit.
    // Already-set fields can't reach here — the modal renders them frozen.
    const submitWorkspaceForm = (form) => {
        const creating = wsModal?.mode !== 'edit';
        if (creating && (form.google || form.meta || form.pixel)) {
            setConfirm({ kind: 'confirmCreateAccounts', form, failMessage: 'Failed to create workspace' });
            return;
        }
        const ws = wsModal?.workspace;
        if (!creating && ws && (
            (form.google && !ws.defaultGoogleAdsAccount)
            || (form.meta && !ws.defaultMetaAdsAccount)
            || (form.pixel && !ws.defaultMetaPixel)
        )) {
            setConfirm({ kind: 'confirmCreateAccounts', editing: true, form, failMessage: 'Failed to update workspace' });
            return;
        }
        saveWorkspace(form);
    };

    const submitMember = async ({ intent, email, workspaceIds, accountLevel, accountRole }) => {
        // Choosing "Owner" in the dialog isn't a save — it opens the transfer
        // confirmation, which is the only thing that can move ownership.
        if (intent === 'transfer') {
            setMemberModal(null);
            setTransfer({ email, mode: ownerCount === 0 ? 'claim' : 'transfer' });
            return;
        }
        const inviting = memberModal?.mode === 'invite';
        setBusy(true);
        try {
            if (inviting) {
                const { data } = await api.post('/team/invites', { email, workspaceIds, accountLevel, accountRole });
                toast.success(data.status === 'granted' ? 'Access granted — the user already has an account.' : 'Invite sent.');
            } else {
                await api.patch('/team/members', { email, workspaceIds, accountLevel, accountRole });
                toast.success('Member updated');
            }
            setMemberModal(null);
            loadTeam();
        } catch (err) {
            toast.error(apiErrorMessage(err, (inviting ? 'Failed to send invite' : 'Failed to update member')));
        } finally {
            setBusy(false);
        }
    };

    // One endpoint, two modes: the server decides which from the account's
    // current owner count, so a race can't turn a claim into a silent takeover.
    const submitTransfer = async ({ email, confirm: typed }) => {
        setBusy(true);
        try {
            const { data } = await api.post('/team/transfer-ownership', { email, confirm: typed });
            toast.success(data.mode === 'claim' ? `${email} now owns this account` : `Ownership transferred to ${email}`);
            // Stepping down changes what /meta reports, and seedAccountRole reads
            // a `user_permissions` cookie only rewritten at login — so a hard
            // refresh would flash the owner UI. Clearing it makes the seed return
            // null ("undecidable"), exactly the case it was designed for: the gate
            // then waits for /meta.
            if (selfEmail && (data.previousOwners || []).map(normalizeEmail).includes(selfEmail)) {
                Cookies.remove('user_permissions');
            }
            setTransfer(null);
            loadTeam();
            revalidateStore();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Failed to transfer ownership'));
        } finally {
            setBusy(false);
        }
    };

    const runConfirm = async () => {
        if (!confirm) return;
        // Re-runs the same PATCH with applyToExisting, so the rewrite goes through
        // the one code path that already knows how to build the payload.
        if (confirm.kind === 'applyWorkspaceAccounts') {
            const form = confirm.form;
            setConfirm(null);
            await saveWorkspace(form, true);
            return;
        }
        // The create-time account confirmation resolves into the ordinary save;
        // the form modal is still open behind it if the user cancels instead.
        if (confirm.kind === 'confirmCreateAccounts') {
            const form = confirm.form;
            setConfirm(null);
            await saveWorkspace(form);
            return;
        }
        // First delete dialog → the type-to-confirm second one. Nothing is
        // deleted yet; the DELETE fires only from deleteWorkspaceConfirm.
        if (confirm.kind === 'deleteWorkspace') {
            setConfirm({ kind: 'deleteWorkspaceConfirm', workspace: confirm.workspace, failMessage: confirm.failMessage });
            return;
        }
        setBusy(true);
        try {
            if (confirm.kind === 'deleteWorkspaceConfirm') {
                await api.delete(`/workspaces/${confirm.workspace.id}`);
                toast.success('Workspace deleted');
                revalidateStore();
            } else if (confirm.kind === 'removeMember') {
                await api.delete('/team/members', { data: { email: confirm.email } });
                toast.success('Member removed');
            } else if (confirm.kind === 'revokeInvite') {
                await api.delete(`/team/invites/${confirm.inviteId}`);
                toast.success('Invite revoked');
            }
            setConfirm(null);
            loadTeam();
            if (confirm.kind === 'deleteWorkspaceConfirm') loadWorkspaces();
        } catch (err) {
            toast.error(apiErrorMessage(err, confirm.failMessage));
        } finally {
            setBusy(false);
        }
    };

    // —— Views ——

    const newWorkspaceButton = canManageAccount ? (
        <Button variant="primary" onClick={() => setWsModal({ mode: 'create' })}>
            <PlusIcon /> New workspace
        </Button>
    ) : null;

    const inviteButton = (
        <Button variant="primary" onClick={() => setMemberModal({ mode: 'invite' })}>
            <PlusIcon /> Invite member
        </Button>
    );

    // Ownership normally moves from the row of whoever is GAINING it. That falls
    // apart whenever the recipient has no row: platform staff are filtered out of
    // the roster, so a staff member who hands the account over loses the only
    // route back. Naming the address covers that — and it is where an owner
    // looks for this anyway, rather than inside someone else's row.
    const transferButton = (
        <Button onClick={() => setTransfer({ email: '', mode: ownerCount === 0 ? 'claim' : 'transfer' })}>
            {ownerCount === 0 ? 'Set owner…' : 'Transfer ownership…'}
        </Button>
    );

    const workspaceColumns = hasAdsAccounts ? 7 : 5;
    // Read-only viewers see who is on the account and what they are, and nothing
    // about the account's access map.
    const memberColumns = readOnly ? 2 : 4;
    // Show the host, not the full URL — the column stays narrow and scannable.
    const websiteLabel = (url) => {
        if (!url) return null;
        try { return new URL(url).host.replace(/^www\./, ''); } catch { return url; }
    };

    // Paged view state. The table always renders — the default workspace is a
    // pinned first row on page 1 (hidden while a search is narrowing, since the
    // server search covers real workspaces only); a search that matches nothing
    // keeps the table (and its search box) on screen with a "no match" row.
    const wsRows = wsPage?.rows || [];
    const wsTotal = wsPage?.total ?? 0;
    const wsFirstLoad = wsPage === null;
    const showDefaultRow = !!accountWs && !wsSearch && (wsPage?.page || 1) === 1;
    const workspaceSearch = (
        <input
            type="search"
            style={ts.search}
            value={wsQuery}
            onChange={(e) => setWsQuery(e.target.value)}
            placeholder="Search workspaces…"
            aria-label="Search workspaces"
        />
    );

    const workspaceView = (
        <>
            <PageHeader
                title="Workspaces"
                subtitle="Give each client its own flows, data sources, knowledge and permanent store. Settings, integrations and credits stay shared across the account."
                action={<div style={ts.headerActions}>{workspaceSearch}{newWorkspaceButton}</div>}
            />

            {!wsFirstLoad && !accountWs && wsTotal === 0 && !wsSearch ? (
                <div style={ts.surface}>
                    <EmptyState
                        message="No workspaces yet — everything lives in the default workspace. Create one to give a client its own flows, data sources and knowledge."
                        action={newWorkspaceButton}
                    />
                </div>
            ) : (
                <div style={ts.surface}>
                    <div style={ts.scroll}>
                        <table style={ts.table}>
                            <thead>
                                <tr>
                                    <th scope="col" style={ts.th}>Workspace</th>
                                    <th scope="col" style={ts.th}>Website</th>
                                    {hasAdsAccounts && <th scope="col" style={ts.th}>Google Ads</th>}
                                    {hasAdsAccounts && <th scope="col" style={ts.th}>Meta ads</th>}
                                    <th scope="col" style={ts.th}>People</th>
                                    <th scope="col" style={ts.th}>Created</th>
                                    <th scope="col" style={{ ...ts.th, textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* The default workspace, pinned first: same cells, edit-only
                                    actions — it can't be deleted, it IS the account. */}
                                {!wsFirstLoad && showDefaultRow && (
                                    <tr key="account" style={ts.row} {...hoverRow}>
                                        <td style={{ ...ts.td(false), ...ts.name }}>
                                            {accountWs.name || 'Account'}
                                            <span style={ts.badgeDefault} title="The account's default workspace — everything not placed in another workspace lives here.">Default</span>
                                        </td>
                                        <td style={ts.td(false)}>
                                            {accountWs.websiteUrl
                                                ? <a href={accountWs.websiteUrl} target="_blank" rel="noreferrer" style={ts.link} title={accountWs.websiteUrl}>{websiteLabel(accountWs.websiteUrl)}</a>
                                                : <span style={ts.muted}>—</span>}
                                        </td>
                                        {hasAdsAccounts && (
                                            <td style={ts.td(false)}>
                                                {googleLabel(accountWs.defaultGoogleAdsAccount) || <span style={ts.muted}>—</span>}
                                            </td>
                                        )}
                                        {hasAdsAccounts && (
                                            <td style={ts.td(false)}>
                                                {metaLabel(accountWs.defaultMetaAdsAccount) || <span style={ts.muted}>—</span>}
                                                {pixelLabel(accountWs) && <div style={ts.sub}>Pixel · {pixelLabel(accountWs)}</div>}
                                            </td>
                                        )}
                                        <td style={{ ...ts.td(false), color: 'var(--text-secondary, #64748b)' }}>{peopleCount('account')}</td>
                                        <td style={{ ...ts.td(false), color: 'var(--text-secondary, #64748b)', whiteSpace: 'nowrap' }}>{formatDate(accountWs.createdAt)}</td>
                                        <td style={ts.td(false)}>
                                            <span style={ts.actionsCell}>
                                                <EditIconButton
                                                    size={28}
                                                    style={ts.rowIcon}
                                                    title="Default workspace settings"
                                                    ariaLabel={`Default workspace settings for ${accountWs.name || 'this account'}`}
                                                    onClick={() => setWsModal({ mode: 'edit', workspace: accountWs })}
                                                />
                                            </span>
                                        </td>
                                    </tr>
                                )}
                                {wsFirstLoad ? <SkeletonRows columns={workspaceColumns} /> : wsRows.length === 0 ? (
                                    <tr>
                                        <td colSpan={workspaceColumns} style={ts.noMatch}>
                                            {wsSearch
                                                ? <>No workspaces match “{wsSearch}”.</>
                                                : <>No other workspaces yet. Create one to give a client its own flows, data sources and knowledge.</>}
                                        </td>
                                    </tr>
                                ) : wsRows.map((w, i) => {
                                    const td = ts.td(i === wsRows.length - 1);
                                    return (
                                        <tr key={w.id} style={ts.row} {...hoverRow}>
                                            <td style={{ ...td, ...ts.name }}>{w.name || w.id}</td>
                                            <td style={td}>
                                                {w.websiteUrl
                                                    ? <a href={w.websiteUrl} target="_blank" rel="noreferrer" style={ts.link} title={w.websiteUrl}>{websiteLabel(w.websiteUrl)}</a>
                                                    : <span style={ts.muted}>—</span>}
                                            </td>
                                            {hasAdsAccounts && (
                                                <td style={td}>
                                                    {googleLabel(w.defaultGoogleAdsAccount) || <span style={ts.muted}>—</span>}
                                                </td>
                                            )}
                                            {hasAdsAccounts && (
                                                <td style={td}>
                                                    {metaLabel(w.defaultMetaAdsAccount) || <span style={ts.muted}>—</span>}
                                                    {pixelLabel(w) && <div style={ts.sub}>Pixel · {pixelLabel(w)}</div>}
                                                </td>
                                            )}
                                            <td style={{ ...td, color: 'var(--text-secondary, #64748b)' }}>{peopleCount(w.id)}</td>
                                            <td style={{ ...td, color: 'var(--text-secondary, #64748b)', whiteSpace: 'nowrap' }}>{formatDate(w.createdAt)}</td>
                                            <td style={td}>
                                                <span style={ts.actionsCell}>
                                                    <EditIconButton
                                                        size={28}
                                                        style={ts.rowIcon}
                                                        title="Workspace settings"
                                                        ariaLabel={`Workspace settings for ${w.name || w.id}`}
                                                        onClick={() => setWsModal({ mode: 'edit', workspace: w })}
                                                    />
                                                    <DeleteIconButton
                                                        size={28}
                                                        style={ts.rowIcon}
                                                        title="Delete workspace"
                                                        ariaLabel={`Delete ${w.name || w.id}`}
                                                        onClick={() => setConfirm({
                                                            kind: 'deleteWorkspace',
                                                            workspace: w,
                                                            failMessage: 'Failed to delete workspace',
                                                        })}
                                                    />
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <Pagination
                        page={wsPage?.page || 1}
                        pageCount={wsPage?.pageCount || 1}
                        total={wsTotal}
                        limit={wsPage?.limit || wsLimit}
                        busy={wsLoading}
                        noun="workspace"
                        label="Workspace pagination"
                        onPageChange={setWsPageNum}
                        onLimitChange={(n) => { setWsLimit(n); setWsPageNum(1); }}
                    />
                </div>
            )}
        </>
    );

    const teamsView = (
        <>
            <PageHeader
                title="Team"
                subtitle={readOnly
                    ? "Everyone on this account. Only an owner or an admin can make changes."
                    : "Invite people, set what they can do, and choose which workspaces they can open. Owners can transfer ownership; admins run the team and workspaces; members just use the app."}
                action={readOnly ? null : (
                    <div style={ts.headerActions}>
                        {canAssignOwner && transferButton}
                        {inviteButton}
                    </div>
                )}
            />

            {/* Accounts created before this model hold no `owner:<shop>` marker at
                all. Everything except transferring ownership still works, so this
                explains rather than alarms — and offers the one-click fix. */}
            {!loading && ownerCount === 0 && members.length > 0 && (
                <div style={{ ...ts.surface, marginBottom: '16px' }}>
                    <p style={ts.notice}>
                        No one owns this account. An owner is needed for transferring ownership;
                        everything else works normally.
                        {canManageAccount && ' Open anyone below and set their role to Owner.'}
                    </p>
                </div>
            )}

            {/* Reachable on legacy accounts: ownership used to be additive, and
                admin store assignment granted it to everyone. A transfer — even
                to the person who should keep it — collapses it back to one. */}
            {!loading && ownerCount > 1 && (
                <div style={{ ...ts.surface, marginBottom: '16px' }}>
                    <p style={ts.notice}>
                        More than one person is recorded as the owner of this account. Transferring ownership —
                        including to yourself — makes one of them the single owner.
                    </p>
                </div>
            )}

            {!loading && members.length === 0 ? (
                <div style={ts.surface}>
                    <EmptyState
                        message="You're the only person on this account. Invite a teammate and pick which workspaces they can open."
                        action={readOnly ? null : inviteButton}
                    />
                </div>
            ) : (
                <div style={ts.surface}>
                    <div style={ts.scroll}>
                        <table style={ts.table}>
                            <thead>
                                <tr>
                                    <th scope="col" style={ts.th}>Member</th>
                                    <th scope="col" style={{ ...ts.th, ...(readOnly ? { width: '100%' } : {}) }}>Role</th>
                                    {!readOnly && <th scope="col" style={{ ...ts.th, width: '100%' }}>Access</th>}
                                    {!readOnly && <th scope="col" style={{ ...ts.th, textAlign: 'right' }}>Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? <SkeletonRows columns={memberColumns} /> : members.map((m, i) => {
                                    const td = ts.td(i === members.length - 1);
                                    const isOwnerRow = m.role === 'owner';
                                    // Nothing on the owner's row is editable — their role is fixed
                                    // and their access is total. Ownership moves from the row of
                                    // whoever is gaining it.
                                    const canEdit = !isOwnerRow && canManageAccount;
                                    return (
                                        <tr key={m.email} style={ts.row} {...hoverRow}>
                                            <td style={td}>
                                                <span style={ts.nameCell}>
                                                    <Monogram label={m.email} />
                                                    <span style={ts.name}>{m.email}</span>
                                                    {normalizeEmail(m.email) === selfEmail && <SelfTag />}
                                                </span>
                                            </td>
                                            <td style={td}><RolePill role={m.role} /></td>
                                            {!readOnly && (
                                                <td style={td}>
                                                    <AccessChips
                                                        fullAccess={m.role !== 'member'}
                                                        accountLevel={m.accountLevel}
                                                        workspaceIds={m.workspaceIds}
                                                        nameOf={workspaceName}
                                                        accountLabel={defaultWorkspaceLabel(accountName)}
                                                    />
                                                </td>
                                            )}
                                            {!readOnly && (
                                                <td style={td}>
                                                    <span style={ts.actionsCell}>
                                                        {canEdit && (
                                                            <EditIconButton
                                                                size={28}
                                                                style={ts.rowIcon}
                                                                title="Edit member"
                                                                ariaLabel={`Edit ${m.email}`}
                                                                onClick={() => setMemberModal({ mode: 'edit', member: m })}
                                                            />
                                                        )}
                                                        {/* The owner is never removable — ownership has to be
                                                            transferred first, which the server enforces too. */}
                                                        {!isOwnerRow && canManageAccount && (
                                                            <DeleteIconButton
                                                                size={28}
                                                                style={ts.rowIcon}
                                                                title="Remove from account"
                                                                ariaLabel={`Remove ${m.email}`}
                                                                onClick={() => setConfirm({
                                                                    kind: 'removeMember',
                                                                    email: m.email,
                                                                    failMessage: 'Failed to remove member',
                                                                })}
                                                            />
                                                        )}
                                                        {isOwnerRow && <span style={ts.muted}>—</span>}
                                                    </span>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {invites.length > 0 && (
                <>
                    <div style={ts.sectionLabel}>Pending invites · {invites.length}</div>
                    <div style={ts.surface}>
                        <div style={ts.scroll}>
                            <table style={ts.table}>
                                <thead>
                                    <tr>
                                        <th scope="col" style={ts.th}>Email</th>
                                        <th scope="col" style={ts.th}>Role</th>
                                        <th scope="col" style={{ ...ts.th, width: '100%' }}>Access</th>
                                        <th scope="col" style={ts.th}>Sent</th>
                                        <th scope="col" style={{ ...ts.th, textAlign: 'right' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invites.map((inv, i) => {
                                        const td = ts.td(i === invites.length - 1);
                                        return (
                                            <tr key={inv._id} style={ts.row} {...hoverRow}>
                                                <td style={td}>
                                                    <span style={ts.nameCell}>
                                                        <Monogram label={inv.email} />
                                                        <span style={ts.name}>{inv.email}</span>
                                                    </span>
                                                </td>
                                                <td style={td}><RolePill role={inv.role} /></td>
                                                <td style={td}>
                                                    <AccessChips
                                                        fullAccess={inv.role === 'admin'}
                                                        accountLevel={inv.accountLevel}
                                                        workspaceIds={inv.workspaceIds}
                                                        nameOf={workspaceName}
                                                        accountLabel={defaultWorkspaceLabel(accountName)}
                                                    />
                                                </td>
                                                <td style={{ ...td, color: 'var(--text-secondary, #64748b)', whiteSpace: 'nowrap' }}>{formatDate(inv.createdAt)}</td>
                                                <td style={td}>
                                                    <span style={ts.actionsCell}>
                                                        <DeleteIconButton
                                                            size={28}
                                                            style={ts.rowIcon}
                                                            title="Revoke invite"
                                                            ariaLabel={`Revoke the invite to ${inv.email}`}
                                                            onClick={() => setConfirm({
                                                                kind: 'revokeInvite',
                                                                inviteId: inv._id,
                                                                email: inv.email,
                                                                failMessage: 'Failed to revoke invite',
                                                            })}
                                                        />
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </>
    );

    return (
        <div style={ts.page}>
            {view === 'teams' ? teamsView : workspaceView}

            {wsModal && (
                <WorkspaceFormModal
                    key={wsModal.workspace?.id || 'new-workspace'}
                    mode={wsModal.mode}
                    workspace={wsModal.workspace}
                    adAccounts={adAccounts}
                    busy={busy}
                    onSubmit={submitWorkspaceForm}
                    onClose={() => { if (!busy) setWsModal(null); }}
                />
            )}

            {memberModal && (
                <MemberAccessModal
                    key={memberModal.member?.email || 'invite-member'}
                    mode={memberModal.mode}
                    email={memberModal.member?.email || ''}
                    accountName={accountName}
                    workspaces={workspaces}
                    initialWorkspaceIds={[...(memberModal.member?.workspaceIds || [])]}
                    initialAccountLevel={memberModal.member?.accountLevel === true}
                    initialRole={memberModal.member?.role || 'member'}
                    canAssignRole={canManageAccount}
                    canAssignOwner={canAssignOwner}
                    busy={busy}
                    onSubmit={submitMember}
                    onClose={() => { if (!busy) setMemberModal(null); }}
                />
            )}

            {transfer && (
                <TransferOwnershipDialog
                    email={transfer.email}
                    mode={transfer.mode}
                    busy={busy}
                    onConfirm={submitTransfer}
                    onClose={() => { if (!busy) setTransfer(null); }}
                />
            )}

            {confirm?.kind === 'deleteWorkspace' && (
                <ConfirmDialog
                    title="Delete workspace"
                    message={`"${confirm.workspace.name || confirm.workspace.id}" and everything inside it — agents, data sources, knowledge entries, uploaded files and data connections — will be permanently deleted for everyone. Run history and dashboards are kept in the default workspace.`}
                    confirmLabel="Continue"
                    busyLabel="Deleting…"
                    busy={busy}
                    onConfirm={runConfirm}
                    onClose={() => { if (!busy) setConfirm(null); }}
                />
            )}

            {confirm?.kind === 'deleteWorkspaceConfirm' && (
                <DeleteWorkspaceDialog
                    name={confirm.workspace.name || confirm.workspace.id}
                    busy={busy}
                    onConfirm={runConfirm}
                    onClose={() => { if (!busy) setConfirm(null); }}
                />
            )}

            {confirm?.kind === 'removeMember' && (
                <ConfirmDialog
                    title="Remove member"
                    message={`${confirm.email} loses access to this account and every workspace in it. You can invite them again later.`}
                    confirmLabel="Remove member"
                    busyLabel="Removing…"
                    busy={busy}
                    onConfirm={runConfirm}
                    onClose={() => { if (!busy) setConfirm(null); }}
                />
            )}

            {confirm?.kind === 'revokeInvite' && (
                <ConfirmDialog
                    title="Revoke invite"
                    message={`The invite to ${confirm.email} stops working. Nothing changes for them if they already signed up.`}
                    confirmLabel="Revoke invite"
                    busyLabel="Revoking…"
                    busy={busy}
                    onConfirm={runConfirm}
                    onClose={() => { if (!busy) setConfirm(null); }}
                />
            )}

            {confirm?.kind === 'confirmCreateAccounts' && (
                <ConfirmDialog
                    title="Lock these ad accounts in?"
                    message={confirm.editing
                        ? (wsModal?.workspace?.isDefault
                            ? "The account you selected becomes the default workspace's permanent default — every agent, data source and Analysis block at this level reads from it. This choice is permanent: it can't be changed or removed later."
                            : "The account you selected becomes this workspace's permanent default — every agent, data source and Analysis block reads from it. This choice is permanent: it can't be changed or removed later.")
                        : "The ad accounts and pixel you selected are wired into this workspace's starter agents and default data sources the moment it's created. This choice is permanent — the accounts can't be changed or removed after the workspace is created."}
                    confirmLabel={confirm.editing ? 'Save changes' : 'Create workspace'}
                    busyLabel={confirm.editing ? 'Saving…' : 'Creating…'}
                    busy={busy}
                    onConfirm={runConfirm}
                    onClose={() => { if (!busy) setConfirm(null); }}
                />
            )}

            {confirm?.kind === 'applyWorkspaceAccounts' && (
                <ConfirmDialog
                    title="Move existing work to the new ad account?"
                    message={`${[
                        confirm.counts.workflows ? `${confirm.counts.workflows} agent${confirm.counts.workflows === 1 ? '' : 's'}` : null,
                        confirm.counts.dataSources ? `${confirm.counts.dataSources} data source${confirm.counts.dataSources === 1 ? '' : 's'}` : null,
                    ].filter(Boolean).join(' and ')} in ${wsModal?.workspace?.isDefault ? 'the default workspace' : 'this workspace'} still point at a different ad account. Continuing repoints them, so future runs report on the account you just chose. Past results are untouched.`}
                    confirmLabel="Update them"
                    busyLabel="Updating…"
                    busy={busy}
                    onConfirm={runConfirm}
                    onClose={() => { if (!busy) setConfirm(null); }}
                />
            )}

        </div>
    );
}
